import { useCallback, useMemo, useState } from 'react';
import { loginUser } from '../api/customerModuleAuth';
import {
  clearAuthToken,
  clearStoredUser,
  decodeJwt,
  getAuthToken,
  getRoleFromToken,
  getStoredUser,
  setAuthToken,
  setStoredUser,
} from '../utils/authStorage';
import { getLocalItem, getSessionItem } from '../utils/safeStorage';

const ADMIN_ACCESS_KEY = 'adminAccessToken';
const LEGACY_ADMIN_ACCESS_KEY = 'hv_admin_token';

const normalizeRole = (role) => {
  if (!role) return '';
  const value = String(role).replace(/-/g, '_').toUpperCase();
  return value === 'SUPER_ADMIN' ? 'ADMIN' : value;
};

export default function useModuleAuth() {
  const [user, setUser] = useState(() => getStoredUser());
  const token = getAuthToken();
  const adminToken =
    getSessionItem(ADMIN_ACCESS_KEY) ||
    getLocalItem(ADMIN_ACCESS_KEY) ||
    getSessionItem(LEGACY_ADMIN_ACCESS_KEY) ||
    getLocalItem(LEGACY_ADMIN_ACCESS_KEY) ||
    '';
  const hasAdminSession = Boolean(adminToken);

  const login = useCallback(async (email, password) => {
    const data = await loginUser(email, password);
    if (data?.token) {
      setAuthToken(data.token);
      setStoredUser(data.user);
      setUser(data.user);
    }
    return data?.user;
  }, []);

  const logout = useCallback(() => {
    clearAuthToken();
    clearStoredUser();
    setUser(null);
  }, []);

  const derivedUser = useMemo(() => {
    if (hasAdminSession) {
      const payload = decodeJwt(adminToken) || decodeJwt(token) || {};
      return {
        ...(user || {}),
        id: user?.id || payload.id,
        email: user?.email || payload.email,
        role: 'ADMIN',
      };
    }
    if (user) return user;
    const payload = decodeJwt(token);
    if (!payload) return null;
    return { id: payload.id, email: payload.email, role: payload.role };
  }, [adminToken, hasAdminSession, token, user]);

  const role = useMemo(() => normalizeRole(derivedUser?.role || getRoleFromToken()), [derivedUser]);

  return {
    token,
    user: derivedUser,
    role,
    isAdmin: role === 'ADMIN',
    login,
    logout,
  };
}
